import { Link } from 'react-router-dom'
import { Zap, DollarSign, Users, Clock, Sparkles, ArrowRight, TrendingUp, Target } from 'lucide-react'
import { motion, useScroll, useTransform } from 'framer-motion'
import { useRef } from 'react'

const Hero = () => {
  const ref = useRef(null)
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"]
  })
  
  const y = useTransform(scrollYProgress, [0, 1], [0, -50])
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0])

  return (
    <section 
      ref={ref}
      className="relative min-h-screen pt-16 lg:pt-20 pb-12 lg:pb-20 overflow-hidden bg-gradient-to-br from-slate-50 via-blue-50/30 to-purple-50/20"
    >
      {/* Animated Background */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-32 w-96 h-96 bg-gradient-radial from-primary-400/20 to-transparent rounded-full animate-float opacity-60" />
        <div className="absolute top-1/2 -left-32 w-64 h-64 bg-gradient-radial from-accent-400/15 to-transparent rounded-full animate-bounce-subtle" />
        <div className="absolute bottom-20 right-1/4 w-48 h-48 bg-gradient-radial from-secondary-400/10 to-transparent rounded-full animate-pulse-slow" />
        
        {/* Grid Pattern */}
        <div className="absolute inset-0 opacity-[0.02]">
          <div className="absolute inset-0" style={{
            backgroundImage: `linear-gradient(rgba(0,0,0,0.1) 1px, transparent 1px),
                            linear-gradient(90deg, rgba(0,0,0,0.1) 1px, transparent 1px)`,
            backgroundSize: '50px 50px'
          }} />
        </div>
      </div>

      <motion.div className="container relative z-10" style={{ y, opacity }}>
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-16 items-center">
          {/* Hero Content */}
          <div className="lg:col-span-7 text-center lg:text-left space-modern">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-primary-50 to-accent-50 rounded-full border border-primary-200/50 text-primary-700 text-sm font-medium mb-6">
                <Sparkles className="w-4 h-4" />
                <span>AI-Powered Hiring Revolution</span>
              </div>
            </motion.div>

            <motion.h1 
              className="text-responsive-xl font-black text-gray-900 mb-6 lg:mb-8 leading-tight"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <span className="text-gradient block mb-2">
                Budget-Smart Hiring
              </span>
              <span className="text-gray-800 block">
                Made Simple with AI
              </span>
            </motion.h1>
            
            <motion.p 
              className="text-responsive-lg text-gray-600 mb-8 lg:mb-10 max-w-2xl mx-auto lg:mx-0 leading-relaxed"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              Transform your hiring process with BudgetHyre's AI interviewer. 
              <span className="font-semibold text-primary-600">Save 80% on screening costs</span> 
              while finding the perfect candidates for your startup.
            </motion.p>
            
            <motion.div 
              className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-8 lg:mb-12"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              <Link 
                to="/demo" 
                className="btn btn-primary text-base px-8 py-4 group relative overflow-hidden"
              >
                <span className="relative z-10 flex items-center gap-2">
                  Start Free Trial
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" />
                </span>
              </Link>
              <Link 
                to="/pricing" 
                className="btn btn-outline text-base px-8 py-4 group"
              >
                <DollarSign className="w-4 h-4 group-hover:animate-pulse" />
                View Pricing
              </Link>
            </motion.div>
            
            <motion.div 
              className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-lg mx-auto lg:mx-0"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
            >
              {[
                { icon: Clock, label: '5min Setup', desc: 'Quick integration' },
                { icon: DollarSign, label: 'Pay-per-use', desc: 'No monthly fees' },
                { icon: Users, label: '80% Savings', desc: 'vs Traditional hiring' }
              ].map((item, index) => (
                <div key={index} className="text-center group">
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-br from-primary-100 to-accent-100 rounded-xl mb-3 group-hover:scale-110 transition-transform duration-200">
                    <item.icon className="w-5 h-5 text-primary-600" />
                  </div>
                  <div className="text-sm font-semibold text-gray-900">{item.label}</div>
                  <div className="text-xs text-gray-500">{item.desc}</div>
                </div>
              ))}
            </motion.div>
          </div>
          
          {/* Hero Visual */}
          <div className="lg:col-span-5 relative">
            <motion.div 
              className="relative max-w-lg mx-auto lg:mx-0"
              initial={{ opacity: 0, x: 30, scale: 0.9 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              transition={{ duration: 1, delay: 0.4, ease: "easeOut" }}
            >
              {/* Main AI Card */}
              <div className="glass-card p-6 lg:p-8 backdrop-blur-xl relative overflow-hidden">
                {/* Header */}
                <div className="flex items-center gap-3 mb-6">
                  <div className="relative">
                    <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-accent-500 rounded-2xl flex items-center justify-center">
                      <Zap className="w-6 h-6 text-white" />
                    </div>
                    <div className="absolute -inset-1 bg-gradient-to-br from-primary-500 to-accent-500 rounded-2xl blur opacity-30 animate-pulse"></div>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">BudgetHyre AI</h3>
                    <p className="text-sm text-gray-500">Smart Interviewer</p>
                  </div>
                </div>

                {/* Chat Messages */}
                <div className="space-y-4">
                  <motion.div 
                    className="bg-gradient-to-r from-primary-500 to-primary-600 text-white px-4 py-3 rounded-2xl rounded-bl-md max-w-xs shadow-lg"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: 1 }}
                  >
                    <div className="flex items-center gap-2 text-sm">
                      <div className="w-2 h-2 bg-green-300 rounded-full animate-pulse"></div>
                      Interviewing Sarah (React Developer)
                    </div>
                  </motion.div>
                  
                  <motion.div 
                    className="bg-white/80 backdrop-blur-sm px-4 py-3 rounded-2xl rounded-br-md ml-8 shadow-sm border border-gray-100"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: 1.3 }}
                  >
                    <div className="text-sm text-gray-700">
                      "Tell me about your experience with React hooks..."
                    </div>
                  </motion.div>
                  
                  <motion.div 
                    className="bg-gradient-to-r from-secondary-500 to-secondary-600 text-white px-4 py-3 rounded-2xl rounded-bl-md max-w-xs shadow-lg"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: 1.6 }}
                  >
                    <div className="flex items-center gap-2 text-sm">
                      <TrendingUp className="w-4 h-4" />
                      95% Match Score
                    </div>
                  </motion.div>
                </div>

                {/* Stats Bar */}
                <motion.div 
                  className="mt-6 pt-4 border-t border-gray-200/50"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 1.9 }}
                >
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">Cost per interview</span>
                    <span className="font-bold text-green-600">$2.50</span>
                  </div>
                  <div className="mt-2 flex items-center gap-2">
                    <div className="flex-1 bg-gray-200 rounded-full h-2">
                      <div className="bg-gradient-to-r from-secondary-500 to-secondary-400 h-2 rounded-full w-3/4"></div>
                    </div>
                    <span className="text-xs text-gray-500">vs $50 traditional</span>
                  </div>
                </motion.div>
              </div>
              
              {/* Floating Elements */}
              <motion.div 
                className="absolute -top-4 -right-4 w-24 h-24 bg-gradient-to-br from-accent-400/20 to-transparent rounded-full blur-xl"
                animate={{ 
                  scale: [1, 1.2, 1],
                  rotate: [0, 180, 360]
                }}
                transition={{ 
                  duration: 8, 
                  repeat: Infinity, 
                  ease: "easeInOut" 
                }}
              />
              
              <motion.div 
                className="absolute -bottom-6 -left-6 w-16 h-16 bg-gradient-to-br from-primary-400/30 to-transparent rounded-full blur-lg"
                animate={{ 
                  y: [0, -20, 0],
                  opacity: [0.3, 0.6, 0.3]
                }}
                transition={{ 
                  duration: 4, 
                  repeat: Infinity, 
                  ease: "easeInOut" 
                }}
              />
            </motion.div>
          </div>
        </div>
      </motion.div>
    </section>
  )
}

export default Hero
